import {
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from "react";

import { IconButton, Modal, Tooltip } from "@mui/material";
import * as pdfjs from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.min.mjs";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { GoDownload } from "react-icons/go";
import { IoClose } from "react-icons/io5";

import { apis, callApi } from "../../../api";
import { downloadFile } from "../../../utils";
import { Loading } from "../Loading/Loading";

import "./image_viewer.scss";

export type TPdfViewerProps = {
	ref: RefObject<TPdfViewerRef | null>;
};
export type TPdfViewerRef = {
	open: ({
		location,
		fileName,
	}: {
		location: string;
		fileName: string;
	}) => void;
	close: () => void;
};

const caches: { [key: string]: string } = {};

export function PdfViewer(props: TPdfViewerProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const pagesRef = useRef<pdfjs.PDFPageProxy[]>([]);
	const [currentScale, setCurrentScale] = useState(1);
	const [index, setIndex] = useState(0);
	const [file, setFile] = useState<{
		location: string;
		name: string;
	} | null>(null);

	const open = useCallback(
		async ({ location, fileName }: { location: string; fileName: string }) => {
			setIsOpen(true);
			setLoading(true);
			setFile({ location, name: fileName });
			let saved = caches[`${location}-${fileName}`];
			if (!saved) {
				const response = await callApi({
					method: "POST",
					url: apis.GET_DOWNLOAD_URL,
					body: {
						fileName,
						location,
					},
				});
				if (!response?.downloadURL) {
					return;
				}

				saved = response.downloadURL;
			}

			const loadingTask = pdfjs.getDocument({ url: saved });
			loadingTask.promise.then((pdf) => {
				const container = document.getElementById("pdf-container");
				if (!container) {
					setLoading(false);
					return;
				}

				for (let i = 0; i < pdf.numPages; i++) {
					const newCanvas = document.createElement("canvas");
					newCanvas.id = `canvas-page-${i + 1}`;
					container.appendChild(newCanvas);

					pdf
						.getPage(i + 1)
						.then((page) => {
							pagesRef.current[i] = page;
							let scale = 1;
							const screenWidth = window.innerWidth;
							let viewport = page.getViewport({ scale: scale });

							if (screenWidth < viewport.width) {
								scale = screenWidth / viewport.width;
								viewport = page.getViewport({ scale: scale });
							}

							setCurrentScale(scale);

							const canvas = document.getElementById(
								`canvas-page-${i + 1}`,
							) as HTMLCanvasElement;
							if (!canvas) {
								return;
							}

							const context = canvas.getContext("2d");

							if (!context) {
								return;
							}

							canvas.height = viewport.height;
							canvas.width = viewport.width;

							// Render PDF page into canvas context
							const renderContext = {
								canvasContext: context,
								viewport: viewport,
							};
							page.render(renderContext);
						})
						.finally(() => {
							setLoading(false);
						});
				}
			});
		},
		[],
	);

	const close = () => {
		setIsOpen(false);
		document.getElementById("pdf-container")?.childNodes.forEach((node) => {
			node.remove();
		});
		pagesRef.current = [];
		setCurrentScale(1);
		setIndex(0);
	};

	useImperativeHandle(props.ref, () => ({ open, close }), [open]);

	const onClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason === "escapeKeyDown") {
			close();
		}
	};

	const download = async () => {
		setLoading(true);
		await downloadFile(file);
		setLoading(false);
	};

	const zoom = (scale: number) => () => {
		if (currentScale > scale) {
			setIndex((prevState) => prevState - 1);
		} else if (currentScale < scale) {
			setIndex((prevState) => prevState + 1);
		}
		setCurrentScale(scale);
		for (let i = 0; i < pagesRef.current.length; i++) {
			if (!pagesRef.current[i]) {
				return;
			}
			const canvas = document.getElementById(
				`canvas-page-${i + 1}`,
			) as HTMLCanvasElement;
			if (!canvas) {
				continue;
			}

			const viewport = pagesRef.current[i].getViewport({ scale });

			const context = canvas.getContext("2d");

			if (!context) {
				return;
			}

			canvas.height = viewport.height;
			canvas.width = viewport.width;

			// Render PDF page into canvas context
			const renderContext = {
				canvasContext: context,
				viewport: viewport,
			};
			pagesRef.current[i].render(renderContext);
		}
	};

	return (
		<Modal open={isOpen} onClose={onClose}>
			<div className="view-media-modal-container">
				<Tooltip title="Close">
					<IconButton className="close-button" onClick={close}>
						<IoClose />
					</IconButton>
				</Tooltip>
				<div id="pdf-container" />
				{loading && <Loading />}
				<div className="pdf-controller">
					<IconButton className="icon-button" onClick={download}>
						<GoDownload />
					</IconButton>
					<IconButton
						className="icon-button"
						disabled={index === -1}
						onClick={zoom(currentScale / 2)}>
						<FaMinus />
					</IconButton>
					<IconButton
						className="icon-button"
						disabled={index === 2}
						onClick={zoom(currentScale * 2)}>
						<FaPlus />
					</IconButton>
				</div>
			</div>
		</Modal>
	);
}
